import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import logoImg from '../assets/logo.png';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (!acceptedTerms) {
      setError('Debes aceptar los Términos y Condiciones y la Política de Privacidad para continuar');
      return;
    }

    setLoading(true);
    const result = await register(email, password, name);
    setLoading(false);

    if (result.success && result.data?.requires_verification) {
      toast.success('¡Cuenta creada! Revisa tu email para verificarla.');
      navigate('/verify-pending', { replace: true, state: { email } });
    } else if (result.success) {
      toast.success('¡Cuenta creada! Vamos a completar tu perfil');
      navigate('/onboarding', { replace: true });
    } else {
      setError(result.error);
      toast.error(result.error);
    }
  };

  const handleGoogleLogin = () => {
    googleLogin();
  };

  return (
    <div className="min-h-screen bg-[hsl(35,25%,91%)] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex flex-col items-center gap-1" data-testid="logo-link">
          <img src={logoImg} alt="Partykatu" className="h-12 w-auto" />
          <span className="font-heading font-semibold text-xl text-[hsl(144,18%,38%)] tracking-tight" translate="no">Partykatu</span>
        </Link>
        <h2 className="mt-6 text-center font-heading font-bold text-2xl sm:text-3xl text-gray-900">
          Crear cuenta
        </h2>
        <p className="mt-2 text-center text-gray-600">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="text-[hsl(144,18%,45%)] hover:text-[hsl(144,18%,38%)] font-medium" data-testid="login-link">
            Inicia sesión
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-2xl sm:px-10 border border-[hsl(35,20%,88%)]">

          {/* Google Login Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full rounded-xl py-3 border-gray-200 font-medium"
            onClick={handleGoogleLogin}
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar con Google
          </Button>

          <div className="mt-6 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">o con email</span>
            </div>
          </div>

          <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm" data-testid="error-message">
                {error}
              </div>
            )}

            <div>
              <Label htmlFor="name" className="text-gray-700">Nombre</Label>
              <div className="mt-1 relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-[hsl(144,18%,45%)]"
                  placeholder="Tu nombre"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-700">Email</Label>
              <div className="mt-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-[hsl(144,18%,45%)]"
                  placeholder="tu@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-700">Contraseña</Label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-[hsl(144,18%,45%)]"
                  placeholder="Mínimo 6 caracteres"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-gray-700">Confirmar contraseña</Label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-[hsl(144,18%,45%)]"
                  placeholder="Repite tu contraseña"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {/* Checkbox legal — obligatorio por RGPD */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[hsl(144,18%,45%)] focus:ring-[hsl(144,18%,45%)] cursor-pointer flex-shrink-0"
                data-testid="accept-terms-checkbox"
              />
              <label htmlFor="acceptTerms" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                He leído y acepto los{' '}
                <Link to="/terminos" target="_blank" className="text-[hsl(144,18%,45%)] hover:underline font-medium">
                  Términos y Condiciones
                </Link>{' '}
                y la{' '}
                <Link to="/privacidad" target="_blank" className="text-[hsl(144,18%,45%)] hover:underline font-medium">
                  Política de Privacidad
                </Link>
                . Soy mayor de 18 años.
              </label>
            </div>

            <Button
              type="submit"
              className="w-full bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-xl py-3 font-medium disabled:opacity-50"
              disabled={loading || !acceptedTerms}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creando cuenta...' : 'Crear cuenta'}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-[hsl(20,8%,50%)]">
          © 2026 Partykatu ·{' '}
          <Link to="/aviso-legal" className="hover:underline">Aviso Legal</Link>
        </p>
      </div>
    </div>
  );
}
